import { useCallback, useEffect, useState } from 'react';
import { adminApi } from './api';
import type { Suggestion } from '../../shared/types';

const POLL_INTERVAL = 15_000;

export interface UseAdminSuggestionResult {
  suggestion: Suggestion | null;
  loading: boolean;
  refresh: () => Promise<void>;
  accept: () => Promise<void>;
  markReviewed: () => Promise<void>;
  reject: (note: string) => Promise<void>;
  reset: () => Promise<boolean>;
}

export function useAdminSuggestion(
  scriptId: string | undefined,
  enabled: boolean,
  onAccepted?: () => void,
): UseAdminSuggestionResult {
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!scriptId || !enabled) return;
    try {
      const res = await adminApi.getActiveSuggestion(scriptId);
      setSuggestion(res.suggestion);
    } catch {
      // soft fail
    } finally {
      setLoading(false);
    }
  }, [scriptId, enabled]);

  useEffect(() => {
    if (!enabled) {
      setSuggestion(null);
      setLoading(false);
      return;
    }
    void refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [enabled, refresh]);

  const accept = useCallback(async () => {
    if (!scriptId || !suggestion) return;
    await adminApi.acceptSuggestion(scriptId, suggestion.id);
    await refresh();
    onAccepted?.();
  }, [scriptId, suggestion, refresh, onAccepted]);

  const markReviewed = useCallback(async () => {
    if (!scriptId || !suggestion) return;
    await adminApi.markSuggestionReviewed(scriptId, suggestion.id);
    await refresh();
  }, [scriptId, suggestion, refresh]);

  const reject = useCallback(
    async (note: string) => {
      if (!scriptId || !suggestion) return;
      await adminApi.rejectSuggestion(scriptId, suggestion.id, note);
      await refresh();
    },
    [scriptId, suggestion, refresh],
  );

  const reset = useCallback(async () => {
    if (!scriptId) return false;
    const result = await adminApi.resetSuggestion(scriptId);
    setSuggestion(null);
    await refresh();
    return result.reset;
  }, [scriptId, refresh]);

  return { suggestion, loading, refresh, accept, markReviewed, reject, reset };
}
